import { useState } from 'react';
import DataTable from '@/components/DataTable.jsx';
import { useSeasonData } from './useSeasonData.js';
import GwBreakdownModal from './GwBreakdownModal.jsx';
import { round2 } from '@/lib/format.js';

const num = (key, label, title) => ({ key, label, title: title ?? label, numeric: true });

/* Points Table — player season totals. */
const pointsColumns = (openBreakdown) => [
	{
		key: 'web_name',
		label: 'Player',
		title: 'Player name',
		strong: true,
		render: (name, row) => (
			<button
				type="button"
				className="link-button"
				onClick={() => openBreakdown({ kind: 'player', id: row.id, name })}
			>
				{name}
			</button>
		),
	},
	{ key: 'team_name', label: 'Team' },
	{ key: 'position', label: 'Pos', title: 'Position', tone: (v) => v },
	{ ...num('total_points', 'Points', 'Total points'), defaultSort: 'desc' },
	num('goals_scored', 'G', 'Goals scored'),
	num('assists', 'A', 'Assists'),
	num('clean_sheets', 'CS', 'Clean sheets'),
	num('expected_goal_involvements_per_90', 'xGI/90', 'Expected goal involvements per 90'),
	num('expected_goals_per_90', 'xG/90', 'Expected goals per 90'),
	num('expected_assists_per_90', 'xA/90', 'Expected assists per 90'),
	num('expected_goals_conceded_per_90', 'xGC/90', 'Expected goals conceded per 90'),
	num('bonus', 'Bonus', 'Bonus points'),
	num('bps', 'BPS', 'Bonus points system'),
	num('points_per_game', 'PPG', 'Points per game'),
	num('minutes', 'Mins', 'Minutes played'),
	{ ...num('now_cost', 'Cost', 'Player cost'), render: (v) => `£${v}` },
	{
		...num('selected_by_percent', 'TSB', 'Teams selected by %'),
		render: (v) => `${v}%`,
	},
	num('goals_conceded', 'GC', 'Goals conceded'),
	num('own_goals', 'OG', 'Own goals'),
	num('penalties_saved', 'PS', 'Penalties saved'),
	num('penalties_missed', 'PM', 'Penalties missed'),
	num('yellow_cards', 'YC', 'Yellow cards'),
	num('red_cards', 'RC', 'Red cards'),
	num('saves', 'S', 'Saves'),
	num('ict_index', 'ICT', 'ICT index'),
	num('ict_index_rank', 'ICT Rank', 'ICT index rank'),
];

export function PointsTable({ season }) {
	const { data, status, error } = useSeasonData(season);
	const [breakdown, setBreakdown] = useState(null);
	return (
		<SeasonTable
			status={status}
			error={error}
			breakdown={breakdown}
			onCloseBreakdown={() => setBreakdown(null)}
			season={season}
			columns={pointsColumns(setBreakdown)}
			rows={data?.history}
			rowKey={(r) => r.id}
			placeholder="Filter by player, team or position"
		/>
	);
}

/* Team xG Data. */
const teamColumns = (openBreakdown) => [
	{
		key: 'team_name',
		label: 'Team',
		strong: true,
		render: (name, row) => (
			<button
				type="button"
				className="link-button"
				onClick={() => openBreakdown({ kind: 'team', id: row.team_id, name })}
			>
				{name}
			</button>
		),
	},
	/* xG leads and sorts. Where the season has none the block drops out and
	   the defaultSort on total_points takes over, which is why it has one. */
	{ ...num('xG', 'xG', 'Expected goals'), render: round2, defaultSort: 'desc' },
	{ ...num('xGI', 'xGI', 'Expected goal involvements'), render: round2 },
	{ ...num('xA', 'xA', 'Expected assists'), render: round2 },
	{ ...num('xGC', 'xGC', 'Expected goals conceded'), render: round2 },
	{ ...num('xG5', 'xG5', 'Expected goals, last five'), render: round2 },
	{ ...num('xGC5', 'xGC5', 'Expected goals conceded, last five'), render: round2 },
	{ ...num('total_points', 'Points', 'Total FPL points'), defaultSort: 'desc' },
	num('goals', 'G', 'Goals scored'),
	num('assists', 'A', 'Assists'),
	num('clean_sheets', 'CS', 'Clean sheets'),
	num('goals_conceded', 'GC', 'Goals conceded'),
	num('own_goals', 'OG', 'Own goals'),
	num('penalties_saved', 'PS', 'Penalties saved'),
	num('penalties_missed', 'PM', 'Penalties missed'),
	num('yellow_cards', 'YC', 'Yellow cards'),
	num('red_cards', 'RC', 'Red cards'),
	num('home_goals_scored', 'Home GF', 'Home goals scored'),
	num('away_goals_scored', 'Away GF', 'Away goals scored'),
	num('home_goals_conceded', 'Home GA', 'Home goals conceded'),
	num('away_goals_conceded', 'Away GA', 'Away goals conceded'),
	num('in_dreamteam', 'DT', 'Dream team selections'),
];

export function TeamTable({ season }) {
	const { data, status, error } = useSeasonData(season);
	const [breakdown, setBreakdown] = useState(null);
	return (
		<SeasonTable
			status={status}
			error={error}
			breakdown={breakdown}
			onCloseBreakdown={() => setBreakdown(null)}
			season={season}
			columns={teamColumns(setBreakdown)}
			rows={data?.team_history}
			rowKey={(r) => r.team_id}
			placeholder="Filter by team"
		/>
	);
}

/* Double Digit Hauls — every ten-plus point return. */
/* The old grid grouped on Fixture rather than listing it. DataTable does not
   group, so it leads and sorts instead: same-match hauls still land together. */
const haulColumns = [
	{ key: 'fixture', label: 'Fixture', defaultSort: 'asc' },
	num('round', 'GW', 'Gameweek'),
	{ key: 'player', label: 'Player', strong: true },
	{ key: 'team', label: 'Team' },
	{ key: 'position', label: 'Pos', title: 'Position', tone: (v) => v },
	num('points', 'Points'),
];

export function HaulsTable({ season }) {
	const { data, status, error } = useSeasonData(season);
	return (
		<SeasonTable
			status={status}
			error={error}
			columns={haulColumns}
			rows={data?.dd_hauls}
			rowKey={(r) => `${r.round}-${r.id}`}
			placeholder="Filter by player, team, position or fixture"
		/>
	);
}

/* Hauls aggregated by fixture, and by player. */
const byFixtureColumns = [
	{ key: 'fixture', label: 'Fixture', strong: true },
	{ ...num('count', 'Hauls', 'Double-digit hauls'), defaultSort: 'desc' },
];

export function HaulsByFixture({ season }) {
	const { data, status, error } = useSeasonData(season);
	return (
		<SeasonTable
			status={status}
			error={error}
			columns={byFixtureColumns}
			rows={data?.dd_agg_fixture}
			rowKey={(r) => r.fixture}
			maxWidth="26rem"
			placeholder="Filter by fixture"
		/>
	);
}

const byPlayerColumns = [
	{ key: 'player', label: 'Player', strong: true },
	{ ...num('count', 'Hauls', 'Double-digit hauls'), defaultSort: 'desc' },
];

export function HaulsByPlayer({ season }) {
	const { data, status, error } = useSeasonData(season);
	return (
		<SeasonTable
			status={status}
			error={error}
			columns={byPlayerColumns}
			rows={data?.dd_agg_player}
			rowKey={(r) => r.player}
			maxWidth="26rem"
			placeholder="Filter by player"
		/>
	);
}

/* Shared loading / error / empty wrapper for the season-backed tabs. */
function SeasonTable({ status, error, columns, rows, rowKey, placeholder, maxWidth, breakdown, onCloseBreakdown, season }) {
	if (status === 'loading') {
		return (
			<div className="tab-loading" role="status">
				<span className="spinner" aria-hidden="true" />
				<span>Loading season…</span>
			</div>
		);
	}
	if (status === 'error') {
		return (
			<div className="notice notice--error" role="alert">
				<strong>Could not load that season.</strong>
				<span>{error}</span>
			</div>
		);
	}
	return (
		<>
			<DataTable
				columns={columns}
				rows={rows ?? []}
				rowKey={rowKey}
				filterPlaceholder={placeholder}
				maxHeight="70vh"
				maxWidth={maxWidth}
				emptyMessage="No data recorded for this season."
				hideEmptyColumns
			/>
			{breakdown && (
				<GwBreakdownModal
					kind={breakdown.kind}
					season={season}
					id={breakdown.id}
					name={breakdown.name}
					onClose={onCloseBreakdown}
				/>
			)}
		</>
	);
}
